import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import SelectableButton, { ISelectableButtonProps } from '@ui/atoms/SelectableButton';
import SelectableButtonGroup, { ISelectableGroupProps } from '@ui/atoms/SelectableButtonGroup';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import DetailText from '@ui/molecules/DetailText';
import SubmitButton from '@ui/molecules/SubmitButton';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { schema } from './InsurancePromptStep.data';

export interface IInsurancePromptStepPageContent {
	pageHeader: string;
	insuranceLabel: string;
	insuranceDescription: string;
	otherLabel: string;
	otherDescription: string;
	governmentLabel: string;
	governmentDescription: string;
}

interface IInsurancePromptStepProps extends IFlowStepComponentProps {
	pageContent: IInsurancePromptStepPageContent;
}

const FullWidthSelectableButton = fullWidth<ISelectableButtonProps>(
	aligned<ISelectableButtonProps>(SelectableButton)
);

const SpacedForm = spaced('form');
const SpacedButtonGroup = spaced<ISelectableGroupProps>(SelectableButtonGroup);

const InsurancePromptStep: FC<IInsurancePromptStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	stepKey,
}) => {
	const {
		pageHeader,
		insuranceLabel,
		insuranceDescription,
		otherLabel,
		otherDescription,
		governmentLabel,
		governmentDescription,
	} = pageContent;
	const [submitting, setSubmitting] = useState(false);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		watch,
		register,
		setValue,
		formState: { errors },
	} = form;

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		let insuranceData = data;
		if (!insuranceData.useInsurance) {
			insuranceData = { ...insuranceData, memberInsuranceId: '' };
		}
		await handleFormSubmission(insuranceData, stepKey);
		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<FlexColumn gap="sm">
				<HeadingText>{pageHeader}</HeadingText>
			</FlexColumn>
			<SpacedForm marginTop="lg" marginBottom="xxxl" onSubmit={handleSubmit(onSubmit)}>
				<SpacedButtonGroup marginBottom="lgxl" gap="sm" layout="column" name="useInsurance">
					<Controller
						name="useInsurance"
						control={control}
						render={({ field: { value, ...field } }) => (
							<>
								<FullWidthSelectableButton
									{...field}
									optionValue={true}
									optionLabel={insuranceLabel}
									value={value ?? ''}
									alignment="start">
									<DetailText gap="xxs" title={insuranceLabel} description={insuranceDescription} />
								</FullWidthSelectableButton>
								<FullWidthSelectableButton
									{...field}
									optionValue={false}
									optionLabel={otherLabel}
									value={value ?? ''}
									alignment="start">
									<DetailText gap="xxs" title={otherLabel} description={otherDescription} />
								</FullWidthSelectableButton>
							</>
						)}
					/>
					<FullWidthSelectableButton
						disabled
						optionValue="government"
						optionLabel={governmentLabel}
						alignment="start">
						<DetailText gap="xxs" title={governmentLabel} description={governmentDescription} />
					</FullWidthSelectableButton>
				</SpacedButtonGroup>
				<NavigationSection>
					<Button label="Back" onClick={() => handleNavBack(stepKey)} />
					<SubmitButton variant="secondary" label="Next" submitting={submitting} />
				</NavigationSection>
			</SpacedForm>
		</CenteredGridItem>
	);
};

export default InsurancePromptStep;
